import ImageSearch from './ImageSearch';
import { AppLink } from '@/shared/components/AppLink';
import type { SearchResultsPresentationVm } from '../viewModels/search.vm';

interface SearchResultsProps {
  searchResults: SearchResultsPresentationVm | null;
  searchQuery: string;
  isLoading: boolean;
  onSuggestionClick: (text: string) => void;
  onTrendingClick: (url: string) => void;
  onProductClick: (url: string) => void;
  onImageSearchClick?: () => void;
  onImageSearchSuccess?: () => void;
  listId?: string;
  className?: string;
}

export default function SearchResults({
  searchResults,
  searchQuery,
  isLoading,
  onSuggestionClick,
  onTrendingClick,
  onProductClick,
  onImageSearchClick,
  onImageSearchSuccess,
  listId = 'search',
  className = '',
}: SearchResultsProps) {
  const hasResults = searchResults && (
    (searchResults.trending?.list.length || 0) > 0 ||
    searchResults.suggestions.length > 0 ||
    searchResults.products.list.length > 0
  );

  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <strong key={i} className="font-semibold">{part}</strong>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  const imageSearchBlock = (
    <ImageSearch onOpen={onImageSearchClick} onSuccess={onImageSearchSuccess} />
  );

  if (isLoading) {
    return (
      <div className={className}>
        <div className="flex flex-col items-center justify-center py-8 gap-2">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
          <p className="text-sm text-gray-500">Searching...</p>
        </div>
        {imageSearchBlock}
      </div>
    );
  }

  if (!hasResults) {
    return (
      <div className={className}>
        <div className="py-2 text-sm text-gray-500">
          {searchQuery.trim() ? 'No suggestions found' : 'Start typing to search...'}
        </div>
        {imageSearchBlock}
      </div>
    );
  }

  return (
    <div className={className}>
      {searchResults.suggestions.length > 0 && (
        <div className="mb-6">
          <ul className="space-y-1" role="list">
            {searchResults.suggestions.map((suggestion, index) => {
              const suggestionText = typeof suggestion === 'string' ? suggestion : suggestion.title;
              return (
                <li key={`${listId}-suggestion-${index}`}>
                  <AppLink
                    to=""
                    shouldRefresh={false}
                    onClick={(e) => {
                      e.preventDefault();
                      onSuggestionClick(suggestionText);
                    }}
                    className="block w-full px-2 py-2 text-left text-sm text-gray-700 transition-colors rounded border-0 bg-transparent hover:bg-gray-100 active:bg-gray-200"
                  >
                    {highlightText(suggestionText, searchQuery)}
                  </AppLink>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {searchResults.trending && searchResults.trending.list.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            {searchResults.trending.trendingTitle}
          </h3>
          <div className="flex flex-wrap gap-2">
            {searchResults.trending.list.map((item, index) => (
              <AppLink
                key={`${listId}-trending-${index}`}
                to={item.url}
                shouldRefresh={true}
                onClick={() => onTrendingClick(item.url)}
                className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                {item.name}
              </AppLink>
            ))}
          </div>
        </div>
      )}

      {searchResults.products.list.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            {searchResults.products.productTitle}
          </h3>
          <div className="grid grid-cols-3 items-start gap-3">
            {searchResults.products.list.map((product, index) => (
              <AppLink
                key={`${listId}-product-${index}`}
                to={product.url}
                shouldRefresh={true}
                onClick={() => onProductClick(product.url)}
                className="group flex h-full flex-col items-start justify-start border-0 bg-transparent p-0 text-left align-top cursor-pointer"
              >
                <div className="aspect-square bg-gray-100 rounded-md overflow-hidden mb-2 border border-gray-200 hover:border-gray-300 transition-colors">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                </div>
                <p className="text-xs text-gray-700 line-clamp-2 group-hover:text-gray-500">
                  {product.name}
                </p>
              </AppLink>
            ))}
          </div>
        </div>
      )}

      {imageSearchBlock}
    </div>
  );
}
